import { useTranslation } from 'react-i18next';
import type { Control, FieldValues } from 'react-hook-form';
import type { PricesUpdate } from '@baerenfang/shared';
import type { AvailabilityCell, CapacityDetails } from '../lib/api';
import { CATEGORIES, NightInputs } from './NightInputs';
import { chf } from '../lib/format';
import { formatDate, sumNight, type NightRow } from '../lib/stayFormHelpers';

// See NightInputs.tsx — Control is invariant in TFieldValues, so we widen
// to FieldValues (Record<string, any>) here too. NightsEditor only binds
// against `nights.N.<cat>` paths via NightInputs, which has the same
// widening, so both StayForm and ExternalStayForm can pass their concrete
// Control without per-page generics or casts at the call site.
type NightsControl = Control<FieldValues>;

type Props = {
  nights: NightRow[];
  control: NightsControl;
  defaultOpenDate: string | null;
  availability: Map<string, AvailabilityCell>;
  capacityErrors: CapacityDetails | null;
  lang: string;
  prices: PricesUpdate;
};

export function NightsEditor({
  nights,
  control,
  defaultOpenDate,
  availability,
  capacityErrors,
  lang,
  prices,
}: Props) {
  const { t } = useTranslation();

  const warningsFor = (n: NightRow) => {
    const cell = availability.get(n.date);
    const headcount = sumNight(n);
    const free = cell?.beds_available ?? Number.POSITIVE_INFINITY;
    const closed = cell?.total_beds === 0;
    const softWarn = closed || headcount > free;
    const serverErr = capacityErrors?.[n.date];
    return { softWarn, serverErr, free: cell?.beds_available, closed };
  };

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900">
      {/* Wide-screen 4-column table */}
      <table className="hidden sm:table w-full text-sm">
        <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          <tr>
            <th className="text-left p-2 font-medium">{t('stayForm.tableHeaders.date')}</th>
            {CATEGORIES.map((cat) => (
              <th key={cat} className="p-2 font-medium">
                <div>{t(`stayForm.cat.${cat}`)}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 font-normal tabular-nums">
                  {chf(prices[cat], lang)}
                </div>
              </th>
            ))}
            <th className="p-2 font-medium text-right">Σ</th>
          </tr>
        </thead>
        <tbody>
          {nights.map((n, i) => {
            const head = sumNight(n);
            const { softWarn, serverErr, free, closed } = warningsFor(n);
            return (
              <tr
                key={n.date}
                className={
                  serverErr
                    ? 'bg-rose-50 dark:bg-rose-950/60'
                    : i % 2
                      ? 'bg-white dark:bg-slate-900'
                      : 'bg-slate-50/40 dark:bg-slate-800/40'
                }
              >
                <td className="p-2 align-middle whitespace-nowrap">
                  <div className="font-medium">{formatDate(n.date, lang)}</div>
                  {softWarn && !serverErr && (
                    <div className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                      {closed
                        ? t('stayForm.softWarn.closed')
                        : t('stayForm.softWarn.notEnoughBeds', { free })}
                    </div>
                  )}
                  {serverErr && (
                    <div className="text-xs text-rose-700 dark:text-rose-300 mt-0.5">
                      {t('stayForm.capacityDetail', {
                        requested: serverErr.requested,
                        available: serverErr.available,
                      })}
                    </div>
                  )}
                </td>
                <NightInputs
                  index={i}
                  control={control}
                  variant="table"
                  prices={prices}
                  lang={lang}
                />
                <td className="p-2 text-right tabular-nums">{head}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Narrow-screen accordion */}
      <div className="sm:hidden divide-y divide-slate-200 dark:divide-slate-700">
        {nights.map((n, i) => {
          const { softWarn, serverErr, free, closed } = warningsFor(n);
          const open = n.date === defaultOpenDate || !!serverErr;
          return (
            <details key={n.date} open={open} className="p-3">
              <summary
                className={
                  'flex justify-between items-center cursor-pointer select-none ' +
                  (serverErr ? 'text-rose-700 dark:text-rose-300' : '')
                }
              >
                <span className="font-medium">{formatDate(n.date, lang)}</span>
                <span className="text-xs text-slate-600 dark:text-slate-400 tabular-nums">
                  {sumNight(n)} {t('stayForm.shortPersons')}
                </span>
              </summary>
              <div className="mt-3 space-y-2">
                <NightInputs
                  index={i}
                  control={control}
                  variant="card"
                  prices={prices}
                  lang={lang}
                />
                {softWarn && !serverErr && (
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    {closed
                      ? t('stayForm.softWarn.closed')
                      : t('stayForm.softWarn.notEnoughBeds', { free })}
                  </p>
                )}
                {serverErr && (
                  <p className="text-xs text-rose-700 dark:text-rose-300">
                    {t('stayForm.capacityDetail', {
                      requested: serverErr.requested,
                      available: serverErr.available,
                    })}
                  </p>
                )}
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}
